import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Copy, Share2, Users, Settings as SettingsIcon } from 'lucide-react';
import { GameSettings } from '../settings/SettingsScreen';
import { SettingsScreen } from '../settings/SettingsScreen';
interface FriendRoomScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onStartGame: (roomCode: string, settings: GameSettings & {
    hideProduct: boolean;
  }) => void;
}
export function FriendRoomScreen({
  isOpen,
  onClose,
  onStartGame
}: FriendRoomScreenProps) {
  const [roomCode, setRoomCode] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [hideProduct, setHideProduct] = useState(false);
  const [settings, setSettings] = useState<GameSettings>({
    timePerRound: 45,
    soundEffects: true,
    music: false,
    zoomLevel: 100
  });
  const generateRoomCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setRoomCode(code);
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText(roomCode);
    // Show toast or feedback
  };
  const handleShare = async () => {
    const shareData = {
      title: 'Join my Connectiply game!',
      text: `Join my game with code: ${roomCode}`,
      url: `https://connectiply.com/join/${roomCode}`
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      handleCopyCode();
    }
  };
  const handleStartGame = () => {
    if (roomCode) {
      onStartGame(roomCode, {
        ...settings,
        hideProduct
      });
      onClose();
    }
  };
  return <>
      <Modal isOpen={isOpen} onClose={onClose} title="Play with Friend" className="max-w-lg" data-id="element-210">
        <div className="space-y-6" data-id="element-211">
          {/* Room Code Section */}
          {!roomCode ? <div className="text-center py-8" data-id="element-212">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-213">
                <Users className="text-white" size={40} data-id="element-214" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-215">
                Host a Game
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-216">
                Create a room and invite your friend to play
              </p>
              <Button fullWidth size="xl" onClick={generateRoomCode} data-id="element-217">
                Generate Room Code
              </Button>
            </div> : <>
              {/* Room Code Display */}
              <Card variant="elevated" className="bg-gradient-to-br from-blue-50 to-violet-50 border-blue-200" data-id="element-218">
                <div className="text-center space-y-3" data-id="element-219">
                  <div className="text-sm font-bold text-slate-600 uppercase tracking-wide" data-id="element-220">
                    Room Code
                  </div>
                  <div className="text-5xl font-black text-slate-900 tracking-wider font-mono" data-id="element-221">
                    {roomCode}
                  </div>
                  <div className="flex gap-2" data-id="element-222">
                    <Button fullWidth variant="outline" onClick={handleCopyCode} className="gap-2" data-id="element-223">
                      <Copy size={18} data-id="element-224" />
                      Copy Code
                    </Button>
                    <Button fullWidth variant="outline" onClick={handleShare} className="gap-2" data-id="element-225">
                      <Share2 size={18} data-id="element-226" />
                      Share
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Game Settings */}
              <Card variant="outline" data-id="element-227">
                <div className="space-y-4" data-id="element-228">
                  <div className="flex items-center justify-between" data-id="element-229">
                    <h4 className="font-bold text-slate-900" data-id="element-230">Game Settings</h4>
                    <button onClick={() => setShowSettings(true)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-231">
                      <SettingsIcon size={20} data-id="element-232" />
                    </button>
                  </div>

                  <div className="space-y-3 text-sm" data-id="element-233">
                    <div className="flex justify-between" data-id="element-234">
                      <span className="text-slate-600" data-id="element-235">Time per round</span>
                      <span className="font-semibold text-slate-900" data-id="element-236">
                        {settings.timePerRound}s
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-237">
                      <span className="text-slate-600" data-id="element-238">Sound effects</span>
                      <span className="font-semibold text-slate-900" data-id="element-239">
                        {settings.soundEffects ? 'On' : 'Off'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-240">
                      <span className="text-slate-600" data-id="element-241">Music</span>
                      <span className="font-semibold text-slate-900" data-id="element-242">
                        {settings.music ? 'On' : 'Off'}
                      </span>
                    </div>
                  </div>

                  {/* Hide Product Toggle */}
                  <div className="pt-3 border-t border-slate-200" data-id="element-243">
                    <button onClick={() => setHideProduct(!hideProduct)} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-244">
                      <div className="text-left" data-id="element-245">
                        <div className="font-semibold text-slate-900" data-id="element-246">
                          Hide Product
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-247">
                          More challenging - no hints
                        </div>
                      </div>
                      <div className={`w-12 h-7 rounded-full transition-colors relative ${hideProduct ? 'bg-blue-500' : 'bg-slate-300'}`} data-id="element-248">
                        <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${hideProduct ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-249" />
                      </div>
                    </button>
                  </div>
                </div>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3" data-id="element-250">
                <Button fullWidth variant="outline" onClick={onClose} data-id="element-251">
                  Cancel
                </Button>
                <Button fullWidth onClick={handleStartGame} data-id="element-252">
                  Start Game
                </Button>
              </div>
            </>}
        </div>
      </Modal>

      <SettingsScreen isOpen={showSettings} onClose={() => setShowSettings(false)} settings={settings} onSave={newSettings => {
      setSettings(newSettings);
      setShowSettings(false);
    }} data-id="element-253" />
    </>;
}